/**
 * Customize Toolbar (requests-dentrix I-13): right-click a classic toolbar
 * to show, hide and reorder its buttons. The layout is saved per user, per
 * mode and per toolbar in the server preferences (`toolbarLayouts`), so it
 * follows the employee to any workstation.
 */
import type { ToolbarLayout } from '@velora/contracts/shell';
import { Button, Dialog } from '@velora/ui';
import { useEffect, useState } from 'react';
import '../skins/skins.css';
import type { ResolvedToolbarButton } from './classicModel.js';

export interface CustomizeToolbarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The mode's buttons for this toolbar, in the mode's order (before the user's layout). */
  buttons: readonly ResolvedToolbarButton[];
  layout: ToolbarLayout | undefined;
  onSave: (layout: ToolbarLayout | null) => void;
}

interface Row {
  id: string;
  label: string;
  shown: boolean;
}

function rowsFor(
  buttons: readonly ResolvedToolbarButton[],
  layout: ToolbarLayout | undefined,
): Row[] {
  const hidden = new Set(layout?.hidden ?? []);
  const rank = new Map((layout?.order ?? []).map((id, i) => [id, i]));
  const rows = buttons.map((b) => ({ id: b.id, label: b.label, shown: !hidden.has(b.id) }));
  if (rank.size === 0) return rows;
  return [...rows].sort(
    (a, b) =>
      (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
}

/** The layout a set of rows describes; null when it equals the mode's own. */
export function layoutFromRows(
  rows: readonly Row[],
  buttons: readonly ResolvedToolbarButton[],
): ToolbarLayout | null {
  const hidden = rows.filter((r) => !r.shown).map((r) => r.id);
  const reordered = rows.some((r, i) => r.id !== buttons[i]?.id);
  if (hidden.length === 0 && !reordered) return null;
  return reordered ? { hidden, order: rows.map((r) => r.id) } : { hidden };
}

export function CustomizeToolbar({
  open,
  onOpenChange,
  buttons,
  layout,
  onSave,
}: CustomizeToolbarProps) {
  const [rows, setRows] = useState<Row[]>(() => rowsFor(buttons, layout));
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (open) {
      setRows(rowsFor(buttons, layout));
      setSelected(0);
    }
  }, [open, buttons, layout]);

  const move = (delta: number) => {
    const to = selected + delta;
    if (to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [row] = next.splice(selected, 1);
    if (!row) return;
    next.splice(to, 0, row);
    setRows(next);
    setSelected(to);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Customize Toolbar"
      description="Choose the buttons on this toolbar and their order. Saved for you on every workstation."
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onSave(null);
              onOpenChange(false);
            }}
          >
            Reset
          </Button>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onSave(layoutFromRows(rows, buttons));
              onOpenChange(false);
            }}
          >
            OK
          </Button>
        </>
      }
    >
      <div className="vel-custtb">
        <ul className="vel-custtb__list" role="listbox" aria-label="Toolbar buttons">
          {rows.map((r, i) => (
            <li
              key={r.id}
              role="option"
              aria-selected={i === selected}
              className="vel-custtb__row"
              onClick={() => setSelected(i)}
            >
              <label>
                <input
                  type="checkbox"
                  checked={r.shown}
                  onChange={(e) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, shown: e.target.checked } : x)))
                  }
                />{' '}
                {r.label}
              </label>
            </li>
          ))}
        </ul>
        <div className="vel-custtb__buttons">
          <Button variant="secondary" onClick={() => move(-1)} disabled={selected === 0}>
            Move Up
          </Button>
          <Button
            variant="secondary"
            onClick={() => move(1)}
            disabled={selected >= rows.length - 1}
          >
            Move Down
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
